import { PATH_CORP_FAQ } from "@capone/common";

import {
  PATH_CARS,
  PATH_ACTIVITIES,
  PATH_FLIGHTS,
  PATH_FLIGHTS_SHOP,
  PATH_HOME,
  PATH_HOTELS,
  PATH_HOTELS_FLIGHTS,
  PATH_HOTELS_FLIGHTS_FLIGHT_SHOP,
  PATH_HOTELS_FLIGHTS_HOTEL_AVAILABILITY,
  PATH_HOTELS_FLIGHTS_HOTEL_SHOP,
  PATH_HOTELS_FLIGHTS_REVIEW,
  PATH_PACKAGES,
  PATH_PACKAGE_FLIGHT_SHOP,
  PATH_PACKAGE_HOTEL_SHOP,
  PATH_PACKAGES_HOTEL_AVAILABILITY,
  PATH_PACKAGE_REVIEW,
  PATH_PREMIER_COLLECTION,
  PATH_PREMIER_COLLECTION_AVAILABILITY,
  PATH_PREMIER_COLLECTION_ANCILLARY_CUSTOMIZE,
  PATH_PREMIER_COLLECTION_SHOP,
  PATH_PREMIER_COLLECTION_SHOP_DETAILS,
  PATH_PREMIER_VACATION_RENTALS,
  PATH_PREMIER_VACATION_RENTALS_SHOP,
  PATH_STAYS,
  PATH_TERMS_OF_SERVICE,
  PATH_TRAVEL_WALLET,
  PATH_TRAVEL_WALLET_V2,
  PATH_TRIPS,
  PATH_CRUISES,
  PATH_VACATION_RENTALS_SHOP,
  isCoreHomepageBookingConfirmationPath,
} from "./urlPaths";

export const HOMEPAGE_ENTRY_PATHS = [
  PATH_HOME,
  PATH_FLIGHTS,
  PATH_HOTELS,
  PATH_STAYS,
  PATH_CARS,
  PATH_PACKAGES,
  PATH_HOTELS_FLIGHTS,
  PATH_ACTIVITIES,
  PATH_CRUISES,
  PATH_PREMIER_COLLECTION,
  PATH_PREMIER_VACATION_RENTALS,
] as const;

const HOMEPAGE_DECISION_PATHS = [
  ...HOMEPAGE_ENTRY_PATHS,
  PATH_FLIGHTS_SHOP,
  PATH_PACKAGES_HOTEL_AVAILABILITY,
  PATH_HOTELS_FLIGHTS_HOTEL_AVAILABILITY,
  PATH_PACKAGE_HOTEL_SHOP,
  PATH_HOTELS_FLIGHTS_HOTEL_SHOP,
  PATH_PACKAGE_FLIGHT_SHOP,
  PATH_HOTELS_FLIGHTS_FLIGHT_SHOP,
  PATH_PACKAGE_REVIEW,
  PATH_HOTELS_FLIGHTS_REVIEW,
  PATH_VACATION_RENTALS_SHOP,
  PATH_PREMIER_COLLECTION_AVAILABILITY,
  PATH_PREMIER_COLLECTION_SHOP,
  PATH_PREMIER_COLLECTION_SHOP_DETAILS,
  PATH_PREMIER_COLLECTION_ANCILLARY_CUSTOMIZE,
  PATH_PREMIER_VACATION_RENTALS_SHOP,
  PATH_TRAVEL_WALLET,
  PATH_TRAVEL_WALLET_V2,
  PATH_TRIPS,
  PATH_CORP_FAQ,
  PATH_TERMS_OF_SERVICE,
] as const;

const normalizePath = (value: string): string =>
  value.length > 1 && value.endsWith("/") ? value.slice(0, -1) : value;

const NORMALIZED_HOMEPAGE_ENTRY_PATHS = new Set(
  HOMEPAGE_ENTRY_PATHS.map(normalizePath)
);
const NORMALIZED_HOMEPAGE_DECISION_PATHS = new Set(
  HOMEPAGE_DECISION_PATHS.map(normalizePath)
);

export const isHomepageEntryPath = (pathname: string): boolean =>
  NORMALIZED_HOMEPAGE_ENTRY_PATHS.has(normalizePath(pathname));

export const isHotelsFunnelPath = (pathname: string): boolean =>
  /^\/hotels\/[^/]+/.test(pathname) && !pathname.includes("/confirm/");

export const isStaysFunnelPath = (pathname: string): boolean =>
  /^\/stays\/[^/]+/.test(pathname) && !pathname.includes("/confirm/");

export const isPremiumOffersFunnelPath = (pathname: string): boolean =>
  /^\/premium-offers\/[^/]+/.test(pathname) && !pathname.includes("/confirm/");

export const isCarsFunnelPath = (pathname: string): boolean =>
  /^\/cars\/[^/]+/.test(pathname) && !pathname.includes("/confirm/");

export const isFlightsFunnelPath = (pathname: string): boolean =>
  /^\/flights\/[^/]+/.test(pathname) && !pathname.includes("/confirm/");

export const isPackagesFunnelPath = (pathname: string): boolean =>
  /^\/(?:packages|hotels-flights)\/[^/]+/.test(pathname) &&
  !pathname.includes("/confirm/") &&
  !pathname.includes("/search");

export const isCorePackagesFunnelPath = (pathname: string): boolean =>
  /^\/packages\/(?:hotels|flights)\//.test(pathname) &&
  !pathname.includes("/confirm/") &&
  !pathname.includes("/search");

export const isPremiumStaysFunnelPath = (pathname: string): boolean =>
  /^\/(?:premium-stays|vacation-rentals)\/[^/]+/.test(pathname) &&
  !pathname.includes("/confirm/") &&
  !pathname.includes("/search");

export const isPremiumStaysVRAvailabilityFunnelPath = (
  pathname: string
): boolean =>
  pathname.startsWith("/premium-stays/vacation-rentals/availability");

export const isPremiumStaysVRShopFunnelPath = (pathname: string): boolean =>
  pathname.startsWith("/premium-stays/vacation-rentals/shop");

export const isVacationRentalsShopFunnelPath = (pathname: string): boolean =>
  pathname.startsWith("/vacation-rentals/shop");

export const isPremiumStaysHotelShopFunnelPath = (pathname: string): boolean =>
  pathname.startsWith("/premium-stays/shop");

export const isPremiumStaysHotelAvailabilityFunnelPath = (
  pathname: string
): boolean => pathname.startsWith("/premium-stays/availability");

export const isPremiumStaysHotelShopDetailsFunnelPath = (
  pathname: string
): boolean => pathname.startsWith("/premium-stays/shop-details");

export const isActivitiesFunnelMobilePath = (pathname: string): boolean =>
  /^\/activities\/[^/]+/.test(pathname) &&
  !pathname.startsWith("/activities/book/") &&
  !pathname.includes("/confirm/");

export const isActivitiesFunnelPath = (pathname: string): boolean =>
  isActivitiesFunnelMobilePath(pathname) &&
  !pathname.startsWith("/activities/shop/");

const isPackagesFunnelPathForDecision = (pathname: string): boolean =>
  /^\/(?:packages|hotels-flights)\/[^/]+/.test(pathname) &&
  !pathname.includes("/confirm/");

const isPremiumStaysFunnelPathForDecision = (pathname: string): boolean =>
  /^\/(?:premium-stays|vacation-rentals)\/[^/]+/.test(pathname) &&
  !pathname.includes("/confirm/");

export const shouldResolveHomepageDecision = (pathname: string): boolean =>
  NORMALIZED_HOMEPAGE_DECISION_PATHS.has(normalizePath(pathname)) ||
  isCoreHomepageBookingConfirmationPath(pathname) ||
  isHotelsFunnelPath(pathname) ||
  isStaysFunnelPath(pathname) ||
  isCarsFunnelPath(pathname) ||
  isFlightsFunnelPath(pathname) ||
  isPackagesFunnelPathForDecision(pathname) ||
  isPremiumStaysFunnelPathForDecision(pathname);
